// src/components/admin/editors/ProjectsEditor.js
import React, { useState, useEffect } from "react";
import { db } from "../../../firebaseConfig/firebase";
import {
  collection,
  doc,
  getDocs,
  setDoc,
  deleteDoc,
} from "firebase/firestore";
import {
  Save,
  Upload,
  X,
  Plus,
  Edit,
  Trash2,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { uploadImage, deleteProjectImages, deleteImageByUrl } from "../../../utils/imageUtils";

const ProjectsEditor = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [projects, setProjects] = useState([]);
  const [editingProject, setEditingProject] = useState(null);
  const [expandedProject, setExpandedProject] = useState(null);

  // Estado para nuevo proyecto
  const emptyProject = {
    title: "",
    description: "",
    shortDescription: "",
    technologies: [""],
    features: [""],
    images: {
      main: "",
      thumbnail: "",
      gallery: [""],
    },
    links: {
      demo: "",
      github: "",
    },
    status: "in-progress",
    featured: false,
    category: "documental",
    startDate: "",
    endDate: "",
  };

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, "projects"));
      const loadedProjects = [];
      querySnapshot.forEach((doc) => {
        loadedProjects.push({ id: doc.id, ...doc.data() });
      });
      setProjects(loadedProjects);
    } catch (error) {
      console.error("Error al cargar proyectos:", error);
      setMessage({ type: "error", text: "Error al cargar los proyectos" });
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e, type, index = null) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setMessage({
        type: "info",
        text: "Convirtiendo a WebP y subiendo imagen...",
      });

      // Eliminar imagen anterior si existe
      if (type === "gallery" && index !== null) {
        const oldImageUrl = editingProject.images.gallery[index];
        if (oldImageUrl) {
          await deleteImageByUrl(oldImageUrl);
        }
      } else if (editingProject.images[type]) {
        await deleteImageByUrl(editingProject.images[type]);
      }

      // Subir nueva imagen (automáticamente convertida a WebP)
      const url = await uploadImage(
        file,
        `projects/${editingProject.title || "new"}-${type}`,
      );

      if (type === "gallery") {
        const newGallery = [...editingProject.images.gallery];
        newGallery[index] = url;
        setEditingProject({
          ...editingProject,
          images: { ...editingProject.images, gallery: newGallery },
        });
      } else {
        setEditingProject({
          ...editingProject,
          images: { ...editingProject.images, [type]: url },
        });
      }
      setMessage({
        type: "success",
        text: "Imagen optimizada y subida correctamente (WebP 90%)",
      });
    } catch (error) {
      console.error("Error al subir imagen:", error);
      setMessage({ type: "error", text: "Error al subir la imagen" });
    }
  };

  const handleSaveProject = async () => {
    if (!editingProject.title) {
      setMessage({ type: "error", text: "El título es obligatorio" });
      return;
    }

    try {
      setSaving(true);
      const projectId = editingProject.id || Date.now().toString();
      const docRef = doc(db, "projects", projectId);
      await setDoc(docRef, { ...editingProject, id: projectId });
      setMessage({ type: "success", text: "Proyecto guardado correctamente" });
      setEditingProject(null);
      loadProjects();
    } catch (error) {
      console.error("Error al guardar proyecto:", error);
      setMessage({ type: "error", text: "Error al guardar el proyecto" });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProject = async (projectId) => {
    const project = projects.find((p) => p.id === projectId);

    if (
      !window.confirm(
        `¿Estás seguro de eliminar el proyecto "${project?.title}"?\n\nEsto eliminará:\n- El proyecto de Firestore\n- Todas sus imágenes de Storage\n\nEsta acción no se puede deshacer.`,
      )
    )
      return;

    try {
      setMessage({ type: "info", text: "Eliminando proyecto e imágenes..." });

      // 1. Eliminar todas las imágenes del proyecto de Storage
      await deleteProjectImages(project);

      // 2. Eliminar el proyecto de Firestore
      await deleteDoc(doc(db, "projects", projectId));

      setMessage({
        type: "success",
        text: "Proyecto e imágenes eliminados correctamente",
      });
      loadProjects();
    } catch (error) {
      console.error("Error al eliminar proyecto:", error);
      setMessage({ type: "error", text: "Error al eliminar el proyecto" });
    }
  };

  const addArrayItem = (field) => {
    setEditingProject({
      ...editingProject,
      [field]: [...editingProject[field], ""],
    });
  };

  const removeArrayItem = (field, index) => {
    const newArray = editingProject[field].filter((_, i) => i !== index);
    setEditingProject({ ...editingProject, [field]: newArray });
  };

  const updateArrayItem = (field, index, value) => {
    const newArray = [...editingProject[field]];
    newArray[index] = value;
    setEditingProject({ ...editingProject, [field]: newArray });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Vista de edición
  if (editingProject) {
    return (
      <div className="max-w-5xl mx-auto">
        {message.text && (
          <div
            className={`mb-6 p-4 rounded-lg ${
              message.type === "success"
                ? "bg-green-50 text-green-800 border border-green-200"
                : message.type === "error"
                  ? "bg-red-50 text-red-800 border border-red-200"
                  : "bg-blue-50 text-blue-800 border border-blue-200"
            }`}
          >
            {message.text}
          </div>
        )}

        <div className="space-y-6">
          {/* Información básica */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Información del Proyecto
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Título *
                </label>
                <input
                  type="text"
                  value={editingProject.title}
                  onChange={(e) =>
                    setEditingProject({
                      ...editingProject,
                      title: e.target.value,
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Nombre del proyecto"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción corta
                </label>
                <input
                  type="text"
                  value={editingProject.shortDescription}
                  onChange={(e) =>
                    setEditingProject({
                      ...editingProject,
                      shortDescription: e.target.value,
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Descripción breve"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción completa
                </label>
                <textarea
                  value={editingProject.description}
                  onChange={(e) =>
                    setEditingProject({
                      ...editingProject,
                      description: e.target.value,
                    })
                  }
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Descripción detallada"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Categoría
                  </label>
                  <select
                    value={editingProject.category}
                    onChange={(e) =>
                      setEditingProject({
                        ...editingProject,
                        category: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="documental">Documental</option>
                    <option value="paisaje">Paisaje</option>
                    <option value="retrato">Retrato</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estado
                  </label>
                  <select
                    value={editingProject.status}
                    onChange={(e) =>
                      setEditingProject({
                        ...editingProject,
                        status: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="completed">Completado</option>
                    <option value="in-progress">En progreso</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha inicio
                  </label>
                  <input
                    type="text"
                    value={editingProject.startDate}
                    onChange={(e) =>
                      setEditingProject({
                        ...editingProject,
                        startDate: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="2024"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha fin
                  </label>
                  <input
                    type="text"
                    value={editingProject.endDate || ""}
                    onChange={(e) =>
                      setEditingProject({
                        ...editingProject,
                        endDate: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="2025 (opcional)"
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={editingProject.featured}
                  onChange={(e) =>
                    setEditingProject({
                      ...editingProject,
                      featured: e.target.checked,
                    })
                  }
                  className="mr-2"
                />
                <label className="text-sm font-medium text-gray-700">
                  Proyecto destacado
                </label>
              </div>
            </div>
          </div>

          {/* Imágenes */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Imágenes
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Imagen principal */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Imagen principal
                </label>
                {editingProject.images.main ? (
                  <div className="relative">
                    <img
                      src={editingProject.images.main}
                      alt="Main"
                      className="w-full h-32 object-cover rounded-lg"
                    />
                    <button
                      onClick={async () => {
                        // Eliminar de Storage
                        if (editingProject.images.main) {
                          await deleteImageByUrl(editingProject.images.main);
                        }
                        // Actualizar estado
                        setEditingProject({
                          ...editingProject,
                          images: { ...editingProject.images, main: "" },
                        });
                      }}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400">
                    <Upload className="text-gray-400 mb-2" size={24} />
                    <span className="text-sm text-gray-600">
                      Subir imagen principal
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, "main")}
                      className="hidden"
                    />
                  </label>
                )}
              </div>

              {/* Thumbnail */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Thumbnail
                </label>
                {editingProject.images.thumbnail ? (
                  <div className="relative">
                    <img
                      src={editingProject.images.thumbnail}
                      alt="Thumbnail"
                      className="w-full h-32 object-cover rounded-lg"
                    />
                    <button
                      onClick={async () => {
                        // Eliminar de Storage
                        if (editingProject.images.thumbnail) {
                          await deleteImageByUrl(editingProject.images.thumbnail);
                        }
                        // Actualizar estado
                        setEditingProject({
                          ...editingProject,
                          images: { ...editingProject.images, thumbnail: "" },
                        });
                      }}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400">
                    <Upload className="text-gray-400 mb-2" size={24} />
                    <span className="text-sm text-gray-600">
                      Subir thumbnail
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, "thumbnail")}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
            </div>

            {/* Galería */}
            <div className="mt-6">
              <div className="flex justify-between items-center mb-4">
                <label className="block text-sm font-medium text-gray-700">
                  Galería
                </label>
                <button
                  onClick={() =>
                    setEditingProject({
                      ...editingProject,
                      images: {
                        ...editingProject.images,
                        gallery: [...editingProject.images.gallery, ""],
                      },
                    })
                  }
                  className="text-blue-600 hover:text-blue-700 text-sm flex items-center"
                >
                  <Plus size={16} className="mr-1" /> Añadir imagen
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {editingProject.images.gallery.map((img, index) => (
                  <div key={index}>
                    {img ? (
                      <div className="relative">
                        <img
                          src={img}
                          alt={`Gallery ${index}`}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                        <button
                          onClick={async () => {
                            // Eliminar de Storage
                            const imageUrl = editingProject.images.gallery[index];
                            if (imageUrl) {
                              await deleteImageByUrl(imageUrl);
                            }

                            // Actualizar estado
                            const newGallery =
                              editingProject.images.gallery.filter(
                                (_, i) => i !== index,
                              );
                            setEditingProject({
                              ...editingProject,
                              images: {
                                ...editingProject.images,
                                gallery: newGallery,
                              },
                            });
                          }}
                          className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400">
                        <Upload className="text-gray-400" size={20} />
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) =>
                            handleImageUpload(e, "gallery", index)
                          }
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Tecnologías y características en dos columnas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Tecnologías */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">
                  Tecnologías
                </h3>
                <button
                  onClick={() => addArrayItem("technologies")}
                  className="text-blue-600 hover:text-blue-700 text-sm flex items-center"
                >
                  <Plus size={16} className="mr-1" /> Añadir
                </button>
              </div>
              <div className="space-y-2">
                {editingProject.technologies.map((tech, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={tech}
                      onChange={(e) =>
                        updateArrayItem("technologies", index, e.target.value)
                      }
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Tecnología"
                    />
                    <button
                      onClick={() => removeArrayItem("technologies", index)}
                      className="text-red-500 hover:text-red-600"
                    >
                      <X size={18} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Características */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">
                  Características
                </h3>
                <button
                  onClick={() => addArrayItem("features")}
                  className="text-blue-600 hover:text-blue-700 text-sm flex items-center"
                >
                  <Plus size={16} className="mr-1" /> Añadir
                </button>
              </div>
              <div className="space-y-2">
                {editingProject.features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={feature}
                      onChange={(e) =>
                        updateArrayItem("features", index, e.target.value)
                      }
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Característica"
                    />
                    <button
                      onClick={() => removeArrayItem("features", index)}
                      className="text-red-500 hover:text-red-600"
                    >
                      <X size={18} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Enlaces */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Enlaces
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Demo / Behance
                </label>
                <input
                  type="url"
                  value={editingProject.links.demo}
                  onChange={(e) =>
                    setEditingProject({
                      ...editingProject,
                      links: { ...editingProject.links, demo: e.target.value },
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="https://behance.net/..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Instagram / Redes
                </label>
                <input
                  type="url"
                  value={editingProject.links.github}
                  onChange={(e) =>
                    setEditingProject({
                      ...editingProject,
                      links: {
                        ...editingProject.links,
                        github: e.target.value,
                      },
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="https://instagram.com/..."
                />
              </div>
            </div>
          </div>

          {/* Botones de acción */}
          <div className="flex justify-between">
            <button
              onClick={() => setEditingProject(null)}
              className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-all"
            >
              Cancelar
            </button>
            <button
              onClick={handleSaveProject}
              disabled={saving}
              className="flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50"
            >
              <Save size={20} />
              <span>{saving ? "Guardando..." : "Guardar proyecto"}</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Vista de lista
  return (
    <div className="max-w-5xl mx-auto">
      {message.text && (
        <div
          className={`mb-6 p-4 rounded-lg ${
            message.type === "success"
              ? "bg-green-50 text-green-800 border border-green-200"
              : "bg-red-50 text-red-800 border border-red-200"
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">
          Proyectos ({projects.length})
        </h2>
        <button
          onClick={() => setEditingProject({ ...emptyProject })}
          className="flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-purple-700"
        >
          <Plus size={20} />
          <span>Nuevo Proyecto</span>
        </button>
      </div>

      <div className="space-y-4">
        {projects.map((project) => (
          <div
            key={project.id}
            className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    {project.images?.main && (
                      <img
                        src={project.images.main}
                        alt={project.title}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                    )}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">
                        {project.title}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {project.category} • {project.startDate}
                        {project.endDate && ` - ${project.endDate}`}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() =>
                      setExpandedProject(
                        expandedProject === project.id ? null : project.id,
                      )
                    }
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    {expandedProject === project.id ? (
                      <ChevronUp size={20} />
                    ) : (
                      <ChevronDown size={20} />
                    )}
                  </button>
                  <button
                    onClick={() => setEditingProject(project)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                  >
                    <Edit size={20} />
                  </button>
                  <button
                    onClick={() => handleDeleteProject(project.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>

              {expandedProject === project.id && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600 mb-2">
                    {project.shortDescription}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {project.technologies?.map((tech, i) => (
                      <span
                        key={i}
                        className="px-2 py-1 bg-blue-50 text-blue-600 text-xs rounded"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}

        {projects.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No hay proyectos. Crea tu primer proyecto.
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectsEditor;
